import './game.css';
import { relatedToolsMarkup, siteBrandMarkup } from '../site-chrome';
import { kakaoCardShareMessage, preloadKakaoShare, shareKakaoInvite } from '../kakao-share';
import {
  FIXED_STEP_SECONDS,
  GAME_HEIGHT,
  GAME_WIDTH,
  WIN_SCORE,
  cloneGameState,
  cpuInput,
  createGameState,
  startMatch,
  stepGame,
  type GameEvent,
  type GameState,
  type PlayerInput,
  type PlayerSide,
} from './core';
import {
  WavebeanNetwork,
  wavebeanPublicOrigin,
  type WavebeanConnectionState,
} from './network';
import {
  isRoomCode,
  normalizeRoomCode,
  type RoomClosedReason,
  type ServerMessage,
} from './protocol';
import {
  applyVisualEvents,
  createVisualState,
  drawGame,
  updateVisualState,
} from './renderer';

type GameMode = 'cpu' | 'online';

const mutableInput = (): PlayerInput => ({
  left: false,
  right: false,
  jump: false,
  attack: false,
});

function requireValue<T>(value: T | null, message: string): T {
  if (value === null) throw new Error(message);
  return value;
}

function mix(from: number, to: number, amount: number): number {
  return from + (to - from) * amount;
}

function interpolateGameState(from: GameState, to: GameState, amount: number): GameState {
  const state = cloneGameState(to);
  for (const side of [0, 1] as const) {
    state.players[side].x = mix(from.players[side].x, to.players[side].x, amount);
    state.players[side].y = mix(from.players[side].y, to.players[side].y, amount);
    state.players[side].vx = mix(from.players[side].vx, to.players[side].vx, amount);
    state.players[side].vy = mix(from.players[side].vy, to.players[side].vy, amount);
  }
  state.ball.x = mix(from.ball.x, to.ball.x, amount);
  state.ball.y = mix(from.ball.y, to.ball.y, amount);
  state.ball.rotation = mix(from.ball.rotation, to.ball.rotation, amount);
  return state;
}

function inviteRoomFromLocation(): string | null {
  const value = normalizeRoomCode(new URL(window.location.href).searchParams.get('room') || '');
  return isRoomCode(value) ? value : null;
}

export function mountWavebeanGame(): void {
  preloadKakaoShare();
  const app = document.querySelector<HTMLDivElement>('#app');
  if (!app) throw new Error('게임 화면을 찾지 못했습니다.');
  document.documentElement.classList.add('wavebean-page');
  document.title = '파도콩 배구 | 오늘툴';

  app.innerHTML = `
    <div class="wavebean-shell">
      <header class="site-brand-header wavebean-header">
        ${siteBrandMarkup()}
        <div class="wavebean-brand" aria-label="파도콩 배구">
          <span aria-hidden="true">●</span><strong>WAVEBEAN VOLLEY</strong>
        </div>
        <div class="wavebean-actions">
          <span id="wavebean-mode-badge" class="wavebean-mode-badge">1P VS CPU</span>
          <button id="wavebean-sound" class="wavebean-icon-button" type="button" aria-label="효과음 끄기" title="효과음 끄기">♪</button>
          <button id="wavebean-pause" class="wavebean-icon-button" type="button" aria-label="일시정지" title="일시정지" disabled>Ⅱ</button>
        </div>
      </header>

      <main class="wavebean-main">
        <section class="wavebean-intro">
          <span>CPU PRACTICE · PRIVATE ONLINE MATCH</span>
          <h1>파도콩 배구</h1>
          <p>먼저 ${WIN_SCORE}점을 내면 승리! 점프하고, 꾹 눌러 스매시하세요.</p>
        </section>

        <section class="wavebean-card" aria-label="파도콩 배구 게임">
          <div class="wavebean-scoreboard" aria-live="polite">
            <div class="wavebean-score wavebean-score-one">
              <span id="wavebean-left-label">나</span>
              <strong id="wavebean-left-score">0</strong>
            </div>
            <div class="wavebean-round">
              <span>ROUND</span>
              <b id="wavebean-round">1</b>
            </div>
            <div class="wavebean-score wavebean-score-two">
              <strong id="wavebean-right-score">0</strong>
              <span id="wavebean-right-label">CPU</span>
            </div>
          </div>

          <div class="wavebean-stage">
            <canvas id="wavebean-canvas" width="${GAME_WIDTH}" height="${GAME_HEIGHT}" aria-label="파도콩 배구 경기 화면">
              Canvas를 지원하는 브라우저가 필요합니다.
            </canvas>
            <div id="wavebean-overlay" class="wavebean-overlay"></div>
            <div id="wavebean-point" class="wavebean-point" role="status" aria-live="polite"></div>
          </div>

          <div class="wavebean-touch-controls" aria-label="모바일 터치 조작">
            <button type="button" data-wavebean-control="left" aria-label="왼쪽으로 이동">◀</button>
            <button type="button" data-wavebean-control="right" aria-label="오른쪽으로 이동">▶</button>
            <button class="jump" type="button" data-wavebean-control="jump">점프</button>
            <button class="attack" type="button" data-wavebean-control="attack">스매시</button>
          </div>
        </section>

        <section class="wavebean-help" aria-label="게임 조작법">
          <div>
            <span class="wavebean-player-dot">나</span>
            <p><kbd>A D / ← →</kbd><small>이동</small></p>
            <p><kbd>W / ↑</kbd><small>점프</small></p>
            <p><kbd>S / ↓</kbd><small>스매시</small></p>
          </div>
          <aside>
            <strong>스매시는 꾹 누르기</strong>
            <span>공보다 먼저 점프하면 더 강해요</span>
          </aside>
          <div class="wavebean-opponent-help">
            <span id="wavebean-opponent-dot" class="wavebean-player-dot">CPU</span>
            <p id="wavebean-opponent-copy">난이도 보통 · 오른쪽 코트를 지켜요</p>
          </div>
        </section>

        ${relatedToolsMarkup('wavebean-volley')}
      </main>

      <footer class="wavebean-footer">
        <span id="wavebean-footer-status">CPU 대전은 SPACE로 일시정지할 수 있어요</span>
      </footer>
    </div>`;

  const missingElement = '파도콩 배구 구성 요소를 찾지 못했습니다.';
  const canvas = requireValue(document.querySelector<HTMLCanvasElement>('#wavebean-canvas'), missingElement);
  const overlay = requireValue(document.querySelector<HTMLDivElement>('#wavebean-overlay'), missingElement);
  const point = requireValue(document.querySelector<HTMLDivElement>('#wavebean-point'), missingElement);
  const pauseButton = requireValue(document.querySelector<HTMLButtonElement>('#wavebean-pause'), missingElement);
  const soundButton = requireValue(document.querySelector<HTMLButtonElement>('#wavebean-sound'), missingElement);
  const modeBadge = requireValue(document.querySelector<HTMLElement>('#wavebean-mode-badge'), missingElement);
  const leftScore = requireValue(document.querySelector<HTMLElement>('#wavebean-left-score'), missingElement);
  const rightScore = requireValue(document.querySelector<HTMLElement>('#wavebean-right-score'), missingElement);
  const round = requireValue(document.querySelector<HTMLElement>('#wavebean-round'), missingElement);
  const leftLabel = requireValue(document.querySelector<HTMLElement>('#wavebean-left-label'), missingElement);
  const rightLabel = requireValue(document.querySelector<HTMLElement>('#wavebean-right-label'), missingElement);
  const opponentDot = requireValue(document.querySelector<HTMLElement>('#wavebean-opponent-dot'), missingElement);
  const opponentCopy = requireValue(document.querySelector<HTMLElement>('#wavebean-opponent-copy'), missingElement);
  const footerStatus = requireValue(document.querySelector<HTMLElement>('#wavebean-footer-status'), missingElement);
  const context = requireValue(canvas.getContext('2d'), 'Canvas 2D를 사용할 수 없습니다.');

  const incomingRoom = inviteRoomFromLocation();
  let game = createGameState();
  let displayGame = game;
  let onlineFrom = cloneGameState(game);
  let onlineTarget = cloneGameState(game);
  let onlineSnapshotAt = performance.now();
  const visual = createVisualState();
  const input = mutableInput();
  let mode: GameMode = incomingRoom ? 'online' : 'cpu';
  let onlineSide: PlayerSide | null = null;
  let currentRoomCode: string | null = incomingRoom;
  let peerConnected = false;
  let paused = false;
  let soundOn = true;
  let rematchReadySides: PlayerSide[] = [];
  let audioContext: AudioContext | null = null;
  let previous = performance.now();
  let accumulator = 0;

  const network = new WavebeanNetwork({
    onConnection: handleConnection,
    onMessage: handleServerMessage,
  });

  function tone(frequency: number, duration = 0.07, wave: OscillatorType = 'sine'): void {
    if (!soundOn) return;
    try {
      const audio = audioContext ?? new AudioContext();
      audioContext = audio;
      void audio.resume();
      const oscillator = audio.createOscillator();
      const gain = audio.createGain();
      oscillator.type = wave;
      oscillator.frequency.setValueAtTime(frequency, audio.currentTime);
      gain.gain.setValueAtTime(0.055, audio.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, audio.currentTime + duration);
      oscillator.connect(gain);
      gain.connect(audio.destination);
      oscillator.start();
      oscillator.stop(audio.currentTime + duration);
    } catch {
      // The game stays playable without Web Audio.
    }
  }

  function clearInput(): void {
    input.left = false;
    input.right = false;
    input.jump = false;
    input.attack = false;
    document.querySelectorAll('[data-wavebean-control].pressed').forEach((button) => {
      button.classList.remove('pressed');
    });
    if (mode === 'online') network.sendInput(input);
  }

  function resizeCanvas(): void {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = GAME_WIDTH * dpr;
    canvas.height = GAME_HEIGHT * dpr;
    context.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  function updateHud(): void {
    leftScore.textContent = String(game.scores[0]);
    rightScore.textContent = String(game.scores[1]);
    round.textContent = String(Math.min(game.scores[0] + game.scores[1] + 1, 13));
    if (mode === 'online') {
      leftLabel.textContent = onlineSide === 1 ? '상대방' : '나';
      rightLabel.textContent = onlineSide === 1 ? '나' : '상대방';
      opponentDot.textContent = '상대';
      opponentCopy.textContent = currentRoomCode
        ? `방 ${currentRoomCode} · 서버 동기화 대전`
        : '초대 링크로 입장한 상대와 겨뤄요';
      modeBadge.textContent = currentRoomCode ? `ONLINE · ${currentRoomCode}` : 'ONLINE 1 VS 1';
      footerStatus.textContent = '온라인 경기는 경기 서버가 점수와 승자를 확정해요';
    } else {
      leftLabel.textContent = '나';
      rightLabel.textContent = 'CPU';
      opponentDot.textContent = 'CPU';
      opponentCopy.textContent = '난이도 보통 · 오른쪽 코트를 지켜요';
      modeBadge.textContent = '1P VS CPU';
      footerStatus.textContent = 'CPU 대전은 SPACE로 일시정지할 수 있어요';
    }
    pauseButton.disabled = mode === 'online' || !['playing', 'point'].includes(game.phase);
  }

  function setRoomInAddress(roomCode: string | null): void {
    const url = new URL(window.location.href);
    if (roomCode) url.searchParams.set('room', roomCode);
    else url.searchParams.delete('room');
    window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
  }

  function inviteUrl(roomCode: string): string {
    const url = new URL('/games/wavebean-volley/', wavebeanPublicOrigin());
    url.searchParams.set('room', roomCode);
    return url.toString();
  }

  function modeSwitchMarkup(): string {
    return `
      <div class="wavebean-mode-switch" aria-label="게임 모드 선택">
        <button type="button" data-wavebean-mode="cpu" class="${mode === 'cpu' ? 'active' : ''}">CPU와 연습</button>
        <button type="button" data-wavebean-mode="online" class="${mode === 'online' ? 'active' : ''}">온라인 대전</button>
      </div>`;
  }

  function renderLobby(): void {
    paused = false;
    rematchReadySides = [];
    point.classList.remove('visible');
    overlay.classList.add('visible');
    overlay.classList.remove('celebrate');
    overlay.innerHTML = `
      <div class="wavebean-overlay-card wavebean-lobby-card ${mode === 'cpu' ? 'wavebean-cpu-lobby' : 'wavebean-online-lobby'}">
        <span class="wavebean-eyebrow">WAVEBEAN BEACH MATCH</span>
        <h2>파도콩 배구</h2>
        <p>CPU와 연습하거나 초대 링크로 친구와 대결하세요.</p>
        ${modeSwitchMarkup()}
        ${mode === 'cpu' ? `
          <button class="wavebean-primary" type="button" data-wavebean-action="start-cpu">CPU 대전 시작 <span>→</span></button>
          <small>키보드와 모바일 터치 조작을 모두 지원해요</small>
        ` : `
          <div class="wavebean-online-actions">
            <button class="wavebean-primary" type="button" data-wavebean-action="create-room">방 만들기 <span>＋</span></button>
            <form id="wavebean-join-form">
              <label for="wavebean-room-code">초대 코드로 참가</label>
              <div><input id="wavebean-room-code" maxlength="6" minlength="6" autocomplete="off" inputmode="text" placeholder="ABC234" aria-label="6자리 방 코드" /><button type="submit">참가</button></div>
            </form>
          </div>
          <small>상대방이 들어오면 자동으로 경기가 시작돼요</small>
        `}
      </div>`;
    bindOverlayEvents();
    updateHud();
  }

  function renderConnecting(state: 'connecting' | 'reconnecting'): void {
    overlay.classList.add('visible');
    overlay.classList.remove('celebrate');
    overlay.innerHTML = `
      <div class="wavebean-overlay-card compact">
        <span class="wavebean-eyebrow">ONLINE 1 VS 1</span>
        <div class="wavebean-loader" aria-hidden="true"></div>
        <h2>${state === 'reconnecting' ? '다시 연결 중' : '서버 연결 중'}</h2>
        <p>${state === 'reconnecting' ? '잠시만 기다리면 같은 선수 자리로 돌아갑니다.' : '안전한 온라인 경기 방을 준비하고 있어요.'}</p>
        <button class="wavebean-text-button" type="button" data-wavebean-action="online-lobby">취소</button>
      </div>`;
    bindOverlayEvents();
  }

  function renderWaitingRoom(roomCode: string): void {
    const link = inviteUrl(roomCode);
    overlay.classList.add('visible');
    overlay.classList.remove('celebrate');
    overlay.innerHTML = `
      <div class="wavebean-overlay-card">
        <span class="wavebean-eyebrow">PRIVATE MATCH READY</span>
        <h2>친구 기다리는 중</h2>
        <p>아래 링크를 보내면 친구가 바로 참가할 수 있어요.</p>
        <strong class="wavebean-room-code" aria-label="방 코드 ${roomCode}">${roomCode}</strong>
        <div class="wavebean-invite-row">
          <input id="wavebean-invite-link" value="${link}" readonly aria-label="초대 링크" />
          <button type="button" data-wavebean-action="copy-link">복사</button>
        </div>
        <button class="wavebean-primary wavebean-share" type="button" data-wavebean-action="share-link">카카오톡으로 초대 <span>↗</span></button>
        <p id="wavebean-online-status" class="wavebean-online-status" role="status">상대방이 들어오면 자동으로 시작해요</p>
        <button class="wavebean-text-button" type="button" data-wavebean-action="leave-online">방 나가기</button>
      </div>`;
    bindOverlayEvents();
  }

  function renderPeerDisconnected(deadline?: number): void {
    const seconds = deadline ? Math.max(1, Math.ceil((deadline - Date.now()) / 1_000)) : 20;
    overlay.classList.add('visible');
    overlay.classList.remove('celebrate');
    overlay.innerHTML = `
      <div class="wavebean-overlay-card compact">
        <span class="wavebean-eyebrow">CONNECTION PAUSED</span>
        <div class="wavebean-loader" aria-hidden="true"></div>
        <h2>상대방 연결 대기</h2>
        <p>경기는 잠시 멈췄어요. 약 ${seconds}초 안에 돌아오면 그대로 계속합니다.</p>
        <button class="wavebean-text-button" type="button" data-wavebean-action="leave-online">경기 나가기</button>
      </div>`;
    bindOverlayEvents();
  }

  function renderOnlineError(message: string): void {
    clearInput();
    overlay.classList.add('visible');
    overlay.classList.remove('celebrate');
    overlay.innerHTML = `
      <div class="wavebean-overlay-card compact">
        <span class="wavebean-eyebrow">ONLINE MATCH ENDED</span>
        <h2>연결할 수 없어요</h2>
        <p>${message}</p>
        <button class="wavebean-primary" type="button" data-wavebean-action="online-lobby">온라인 대전으로 <span>→</span></button>
        <button class="wavebean-text-button" type="button" data-wavebean-action="cpu-lobby">CPU와 연습하기</button>
      </div>`;
    bindOverlayEvents();
  }

  function renderPaused(): void {
    overlay.classList.add('visible');
    overlay.innerHTML = `
      <div class="wavebean-overlay-card compact">
        <span class="wavebean-eyebrow">PAUSED</span>
        <h2>잠깐 쉬는 중</h2>
        <button class="wavebean-primary" type="button" data-wavebean-action="resume">계속하기 <span>▶</span></button>
        <button class="wavebean-text-button" type="button" data-wavebean-action="cpu-lobby">모드 선택으로</button>
      </div>`;
    bindOverlayEvents();
  }

  function renderGameover(): void {
    const won = mode === 'online' ? game.winner === onlineSide : game.winner === 0;
    const winnerLabel = won ? '내' : mode === 'cpu' ? 'CPU' : '상대방';
    const ownReady = onlineSide !== null && rematchReadySides.includes(onlineSide);
    const opponentReady = onlineSide !== null && rematchReadySides.some((side) => side !== onlineSide);
    overlay.classList.add('visible', 'celebrate');
    overlay.innerHTML = `
      <div class="wavebean-confetti" aria-hidden="true">✦　●　✧　▲　✦</div>
      <div class="wavebean-overlay-card">
        <span class="wavebean-eyebrow">MATCH COMPLETE</span>
        <h2>${winnerLabel} 승리!</h2>
        <p>${game.scores[0]} : ${game.scores[1]} · ${opponentReady ? '상대방이 재대결을 기다리고 있어요' : '멋진 경기였어요'}</p>
        <button class="wavebean-primary" type="button" data-wavebean-action="rematch" ${ownReady ? 'disabled' : ''}>${ownReady ? '상대방 기다리는 중' : opponentReady ? '재대결 수락' : '한 판 더!'} <span>↻</span></button>
        <button class="wavebean-text-button" type="button" data-wavebean-action="${mode === 'online' ? 'leave-online' : 'cpu-lobby'}">모드 선택으로</button>
      </div>`;
    bindOverlayEvents();
  }

  function startCpuMatch(): void {
    if (currentRoomCode) network.leave();
    mode = 'cpu';
    onlineSide = null;
    currentRoomCode = null;
    peerConnected = false;
    setRoomInAddress(null);
    game = createGameState();
    displayGame = game;
    startMatch(game, Math.random() >= 0.5 ? 1 : 0);
    paused = false;
    overlay.classList.remove('visible', 'celebrate');
    overlay.innerHTML = '';
    clearInput();
    updateHud();
    tone(420, 0.08, 'square');
    window.setTimeout(() => tone(660, 0.1, 'square'), 90);
  }

  function resetToLobby(nextMode: GameMode): void {
    network.leave();
    mode = nextMode;
    onlineSide = null;
    currentRoomCode = null;
    peerConnected = false;
    rematchReadySides = [];
    setRoomInAddress(null);
    game = createGameState();
    displayGame = game;
    clearInput();
    renderLobby();
  }

  function startCreatingRoom(): void {
    mode = 'online';
    currentRoomCode = null;
    onlineSide = null;
    renderConnecting('connecting');
    network.createRoom();
  }

  function startJoiningRoom(roomCode: string): void {
    const normalized = normalizeRoomCode(roomCode);
    if (!isRoomCode(normalized)) {
      const inputElement = overlay.querySelector<HTMLInputElement>('#wavebean-room-code');
      inputElement?.setCustomValidity('올바른 6자리 방 코드를 입력해 주세요.');
      inputElement?.reportValidity();
      return;
    }
    mode = 'online';
    currentRoomCode = normalized;
    setRoomInAddress(normalized);
    renderConnecting('connecting');
    network.joinRoom(normalized);
  }

  async function copyInviteLink(): Promise<void> {
    if (!currentRoomCode) return;
    const value = inviteUrl(currentRoomCode);
    let copied = false;
    try {
      await navigator.clipboard.writeText(value);
      copied = true;
    } catch {
      const inputElement = overlay.querySelector<HTMLInputElement>('#wavebean-invite-link');
      if (inputElement) {
        inputElement.select();
        copied = document.execCommand('copy');
      }
    }
    const status = overlay.querySelector<HTMLElement>('#wavebean-online-status');
    if (status) status.textContent = copied ? '초대 링크를 복사했어요!' : '링크를 길게 눌러 복사해 주세요.';
  }

  async function shareInviteLink(): Promise<void> {
    if (!currentRoomCode) return;
    const mode = await shareKakaoInvite('파도콩 배구 온라인 대전', '파도콩 배구 한 판 할래?', inviteUrl(currentRoomCode));
    const status = overlay.querySelector<HTMLElement>('#wavebean-online-status');
    if (status) status.textContent = kakaoCardShareMessage(mode);
  }

  function bindOverlayEvents(): void {
    overlay.querySelectorAll<HTMLButtonElement>('[data-wavebean-mode]').forEach((button) => {
      button.addEventListener('click', () => {
        mode = button.dataset.wavebeanMode === 'online' ? 'online' : 'cpu';
        renderLobby();
      });
    });
    overlay.querySelector('[data-wavebean-action="start-cpu"]')?.addEventListener('click', startCpuMatch);
    overlay.querySelector('[data-wavebean-action="create-room"]')?.addEventListener('click', startCreatingRoom);
    overlay.querySelector('[data-wavebean-action="copy-link"]')?.addEventListener('click', () => void copyInviteLink());
    overlay.querySelector('[data-wavebean-action="share-link"]')?.addEventListener('click', () => void shareInviteLink());
    overlay.querySelector('[data-wavebean-action="resume"]')?.addEventListener('click', () => {
      paused = false;
      overlay.classList.remove('visible');
      overlay.innerHTML = '';
      updateHud();
    });
    overlay.querySelector('[data-wavebean-action="cpu-lobby"]')?.addEventListener('click', () => resetToLobby('cpu'));
    overlay.querySelector('[data-wavebean-action="online-lobby"]')?.addEventListener('click', () => resetToLobby('online'));
    overlay.querySelector('[data-wavebean-action="leave-online"]')?.addEventListener('click', () => resetToLobby('online'));
    overlay.querySelector('[data-wavebean-action="rematch"]')?.addEventListener('click', () => {
      if (mode === 'cpu') startCpuMatch();
      else {
        network.requestRematch();
        if (onlineSide !== null && !rematchReadySides.includes(onlineSide)) {
          rematchReadySides = [...rematchReadySides, onlineSide];
        }
        renderGameover();
      }
    });
    overlay.querySelector<HTMLFormElement>('#wavebean-join-form')?.addEventListener('submit', (event) => {
      event.preventDefault();
      const inputElement = overlay.querySelector<HTMLInputElement>('#wavebean-room-code');
      startJoiningRoom(inputElement?.value || '');
    });
    overlay.querySelector<HTMLInputElement>('#wavebean-room-code')?.addEventListener('input', (event) => {
      const inputElement = event.currentTarget as HTMLInputElement;
      inputElement.value = normalizeRoomCode(inputElement.value);
      inputElement.setCustomValidity('');
    });
  }

  function handleEvents(events: readonly GameEvent[]): void {
    applyVisualEvents(visual, events);
    for (const event of events) {
      if (event.type === 'jump') tone(300, 0.045, 'square');
      if (event.type === 'wall') tone(170, 0.04, 'square');
      if (event.type === 'hit') tone(240 + Math.min(180, event.strength / 2), 0.05, 'square');
      if (event.type === 'smash') tone(135, 0.1, 'sawtooth');
      if (event.type === 'point') {
        const mine = mode === 'online' ? event.side === onlineSide : event.side === 0;
        point.textContent = mine ? '내 득점!' : mode === 'cpu' ? 'CPU 득점!' : '상대방 득점!';
        point.classList.add('visible');
        tone(115, 0.18, 'sawtooth');
      }
      if (event.type === 'serve') point.classList.remove('visible');
      if (event.type === 'gameover') {
        point.classList.remove('visible');
        renderGameover();
        window.setTimeout(() => tone(520, 0.12, 'square'), 180);
        window.setTimeout(() => tone(780, 0.2, 'square'), 340);
      }
    }
    updateHud();
  }

  function applyOnlineState(state: GameState, events: readonly GameEvent[]): void {
    onlineFrom = cloneGameState(displayGame);
    game = cloneGameState(state);
    onlineTarget = cloneGameState(state);
    onlineSnapshotAt = performance.now();
    handleEvents(events);
    if (game.phase === 'gameover') renderGameover();
    else if (peerConnected) {
      overlay.classList.remove('visible', 'celebrate');
      overlay.innerHTML = '';
    }
  }

  function closedMessage(reason: RoomClosedReason): string {
    if (reason === 'opponent-left') return '상대방이 방을 나가 경기가 종료됐습니다.';
    if (reason === 'opponent-timeout') return '상대방이 제한 시간 안에 돌아오지 않았습니다.';
    if (reason === 'server-shutdown') return '경기 서버가 점검을 위해 종료됐습니다. 잠시 후 다시 시도해 주세요.';
    return '방이 만료됐습니다. 새 방을 만들어 주세요.';
  }

  function handleServerMessage(message: ServerMessage): void {
    if (message.type === 'room') {
      mode = 'online';
      onlineSide = message.side;
      currentRoomCode = message.roomCode;
      game = cloneGameState(message.state);
      displayGame = cloneGameState(message.state);
      onlineFrom = cloneGameState(message.state);
      onlineTarget = cloneGameState(message.state);
      setRoomInAddress(message.roomCode);
      updateHud();
      if (message.status === 'waiting') renderWaitingRoom(message.roomCode);
      else if (message.status === 'gameover') renderGameover();
      return;
    }
    if (message.type === 'peer') {
      peerConnected = message.connected;
      if (!message.connected) renderPeerDisconnected(message.reconnectDeadline);
      else if (game.phase === 'ready' && currentRoomCode) renderWaitingRoom(currentRoomCode);
      else if (game.phase === 'gameover') renderGameover();
      else {
        overlay.classList.remove('visible', 'celebrate');
        overlay.innerHTML = '';
      }
      return;
    }
    if (message.type === 'state') {
      if (message.state.phase !== 'gameover') rematchReadySides = [];
      applyOnlineState(message.state, message.events);
      return;
    }
    if (message.type === 'rematch') {
      rematchReadySides = [...message.readySides];
      renderGameover();
      return;
    }
    if (message.type === 'closed') {
      peerConnected = false;
      renderOnlineError(closedMessage(message.reason));
      return;
    }
    renderOnlineError(message.message);
  }

  function handleConnection(state: WavebeanConnectionState): void {
    if (mode !== 'online') return;
    if (state === 'connecting' || state === 'reconnecting') renderConnecting(state);
    else if (state === 'offline') {
      renderOnlineError('경기 서버에 연결하지 못했습니다. 네트워크를 확인한 뒤 다시 시도해 주세요.');
    }
  }

  function togglePause(): void {
    if (mode !== 'cpu' || !['playing', 'point'].includes(game.phase)) return;
    paused = !paused;
    clearInput();
    pauseButton.textContent = paused ? '▶' : 'Ⅱ';
    pauseButton.setAttribute('aria-label', paused ? '계속하기' : '일시정지');
    pauseButton.title = paused ? '계속하기' : '일시정지';
    if (paused) renderPaused();
    else {
      overlay.classList.remove('visible');
      overlay.innerHTML = '';
      tone(520, 0.06, 'square');
    }
  }

  function setControl(control: keyof PlayerInput, active: boolean): void {
    input[control] = active;
  }

  const keyToControl: Partial<Record<string, keyof PlayerInput>> = {
    KeyA: 'left',
    ArrowLeft: 'left',
    KeyD: 'right',
    ArrowRight: 'right',
    KeyW: 'jump',
    ArrowUp: 'jump',
    KeyS: 'attack',
    ArrowDown: 'attack',
  };

  window.addEventListener('keydown', (event) => {
    if (event.code in keyToControl || event.code === 'Space') event.preventDefault();
    if (event.code === 'Space' && !event.repeat) {
      if (game.phase === 'ready') {
        if (mode === 'cpu') startCpuMatch();
      } else {
        togglePause();
      }
      return;
    }
    const control = keyToControl[event.code];
    if (control) setControl(control, true);
  }, { passive: false });

  window.addEventListener('keyup', (event) => {
    const control = keyToControl[event.code];
    if (control) setControl(control, false);
  });

  window.addEventListener('blur', () => {
    clearInput();
    if (mode === 'cpu' && ['playing', 'point'].includes(game.phase) && !paused) togglePause();
  });

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) clearInput();
  });

  document.querySelectorAll<HTMLButtonElement>('[data-wavebean-control]').forEach((button) => {
    const control = button.dataset.wavebeanControl as keyof PlayerInput;
    button.addEventListener('pointerdown', (event) => {
      event.preventDefault();
      button.setPointerCapture(event.pointerId);
      button.classList.add('pressed');
      setControl(control, true);
    });
    const release = () => {
      button.classList.remove('pressed');
      setControl(control, false);
    };
    button.addEventListener('pointerup', release);
    button.addEventListener('pointercancel', release);
    button.addEventListener('lostpointercapture', release);
  });

  pauseButton.addEventListener('click', togglePause);
  soundButton.addEventListener('click', () => {
    soundOn = !soundOn;
    soundButton.textContent = soundOn ? '♪' : '×';
    soundButton.setAttribute('aria-label', soundOn ? '효과음 끄기' : '효과음 켜기');
    soundButton.title = soundOn ? '효과음 끄기' : '효과음 켜기';
  });
  window.addEventListener('resize', resizeCanvas);

  function frame(now: number): void {
    const deltaSeconds = Math.min(0.05, Math.max(0, (now - previous) / 1_000));
    previous = now;
    if (mode === 'cpu' && !paused && ['playing', 'point'].includes(game.phase)) {
      accumulator = Math.min(accumulator + deltaSeconds, FIXED_STEP_SECONDS * 5);
      while (accumulator >= FIXED_STEP_SECONDS) {
        handleEvents(stepGame(game, [input, cpuInput(game)]));
        accumulator -= FIXED_STEP_SECONDS;
      }
      displayGame = game;
    } else if (mode === 'online') {
      network.sendInput(input);
      const interpolation = Math.min(1, Math.max(0, (now - onlineSnapshotAt) / 50));
      displayGame = interpolateGameState(onlineFrom, onlineTarget, interpolation);
    }
    updateVisualState(visual, deltaSeconds);
    drawGame(context, displayGame, visual, now);
    requestAnimationFrame(frame);
  }

  resizeCanvas();
  updateHud();
  renderLobby();
  requestAnimationFrame(frame);
  if (incomingRoom) startJoiningRoom(incomingRoom);
}
